import { useMemo, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentType,
  ComplianceProgramStep,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";

import TalentIndicatorSidebar from "../talent-indicator-sidebar/talent-indicator-sidebar";
import { ProgramForm, ProgramFormStep } from "../types";
import { toQuestionSetOption } from "./question-set-option";
import QuestionSetSelector from "./question-set-selector";
import { QuestionList, QuestionRow } from "./question-tab-section";
import RoleGoalValueQuestionRows from "./role-goal-value-question-rows";
import { talentIndicatorsSummary } from "./system-question-summaries";

const SelfManagerAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  talentIndicatorData,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  onAddOrUpdateStep,
  showQuestionSetError,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  showQuestionSetError: boolean;
}) => {
  const label = useLabel();

  const [isTalentIndicatorSidebarOpen, setIsTalentIndicatorSidebarOpen] =
    useState(false);
  // Enable opens the sidebar with every indicator switched on, as the switch
  // would.
  const [isEnablingTalentIndicators, setIsEnablingTalentIndicators] =
    useState(false);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(
        ({ assessmentType }) => assessmentType === AssessmentType.Performance,
      )
      .filter((assessmentGroup) => !assessmentGroup.hasNpsQuestions)
      .map(toQuestionSetOption);
  }, [availableAssessmentGroups]);

  const downwardReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.DownwardReview,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return downwardReviewStep && downwardReviewStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === downwardReviewStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, downwardReviewStep]);

  const applicableTalentIndicators = useMemo(() => {
    return talentIndicatorData
      ? talentIndicatorData.filter(
          (talentIndicator) => !talentIndicator.isManagerIndicator,
        )
      : [];
  }, [talentIndicatorData]);

  const talentIndicatorsWhenEnabled = applicableTalentIndicators.map(
    (talentIndicator) => ({
      talentIndicatorId: talentIndicator.id,
      responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
      responseVisibility:
        AssessmentQuestionResponseVisibility.HiddenFromSubject,
      comment: AssessmentQuestionComment.Optional,
      description: talentIndicator.description,
    }),
  );

  return (
    downwardReviewStep && (
      <>
        {isTalentIndicatorSidebarOpen && (
          <TalentIndicatorSidebar
            onClose={() => {
              setIsTalentIndicatorSidebarOpen(false);
              setIsEnablingTalentIndicators(false);
            }}
            canUpdate={canUpdate}
            assessmentType={AssessmentType.Performance}
            talentIndicatorData={applicableTalentIndicators}
            isEnabling={isEnablingTalentIndicators}
            talentIndicatorSetup={
              isEnablingTalentIndicators
                ? {
                    ...downwardReviewStep.talentIndicatorReviewConfig,
                    talentIndicators: talentIndicatorsWhenEnabled,
                  }
                : downwardReviewStep.talentIndicatorReviewConfig
            }
            onUpdateTalentIndicators={(talentIndicatorReviewConfig) => {
              onAddOrUpdateStep({
                ...downwardReviewStep,
                talentIndicatorReviewConfig,
              });
            }}
          />
        )}
        <RoleGoalValueQuestionRows
          step={downwardReviewStep}
          canUpdate={canUpdate}
          onAddOrUpdateStep={onAddOrUpdateStep}
        >
          {(roleGoalValueItems) => (
            <QuestionList
              title={`Self and Manager ${label("review", {
                capitalize: true,
              })}`}
              items={[
                {
                  key: "question-set",
                  added: !!selectedAssessmentGroup,
                  row: (
                    <QuestionSetSelector
                      assessmentType={AssessmentType.Performance}
                      availableAssessmentGroupSelectOptions={
                        availableAssessmentGroupSelectOptions
                      }
                      canUpdate={canUpdate}
                      onUpdateAssessmentTemplate={(assessmentTemplateId) => {
                        onAddOrUpdateStep({
                          ...downwardReviewStep,
                          questionSetIds: assessmentTemplateId
                            ? [assessmentTemplateId]
                            : [],
                        });
                      }}
                      onAssessmentGroupCreated={onAssessmentGroupCreated}
                      onAssessmentGroupArchived={onAssessmentGroupArchived}
                      selectedAssessmentGroup={selectedAssessmentGroup}
                      error={
                        showQuestionSetError &&
                        downwardReviewStep.questionSetIds.length === 0
                          ? "Please choose a question set"
                          : undefined
                      }
                    />
                  ),
                },
                ...roleGoalValueItems,
                {
                  key: "talent-indicators",
                  added:
                    downwardReviewStep.talentIndicatorReviewConfig
                      .talentIndicators.length > 0,
                  unavailable: applicableTalentIndicators.length === 0,
                  row: (
                    <QuestionRow
                      title="Employee Talent Indicators"
                      summary={talentIndicatorsSummary(
                        downwardReviewStep.talentIndicatorReviewConfig,
                        applicableTalentIndicators,
                      )}
                      warning={
                        applicableTalentIndicators.length === 0
                          ? "There was a problem loading talent indicators"
                          : undefined
                      }
                      warningSummary="Talent indicators could not be loaded"
                      added={
                        downwardReviewStep.talentIndicatorReviewConfig
                          .talentIndicators.length > 0
                      }
                      disabled={!canUpdate}
                      onRemove={() =>
                        onAddOrUpdateStep({
                          ...downwardReviewStep,
                          talentIndicatorReviewConfig: {
                            ...downwardReviewStep.talentIndicatorReviewConfig,
                            talentIndicators: [],
                          },
                        })
                      }
                      onEdit={() => setIsTalentIndicatorSidebarOpen(true)}
                      onAdd={() => {
                        setIsEnablingTalentIndicators(true);
                        setIsTalentIndicatorSidebarOpen(true);
                      }}
                      editAriaLabel="Edit employee talent indicators"
                    />
                  ),
                },
              ]}
            />
          )}
        </RoleGoalValueQuestionRows>
      </>
    )
  );
};
export default SelfManagerAssessmentStep;
